import { useId, type InputHTMLAttributes, type JSX, type Ref } from 'react';

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  /** React 19 передаёт `ref` обычным пропом — это позволяет спредить `register()`. */
  ref?: Ref<HTMLInputElement> | undefined;
}

/**
 * Поле формы с подписью и ошибкой. Подпись связана с полем через `htmlFor`,
 * ошибка и подсказка — через `aria-describedby`, ошибка объявляется
 * скринридеру как `role="alert"` (01-SPEC.md §7, «Доступность»).
 */
export function Field({ label, error, hint, ref, ...input }: FieldProps): JSX.Element {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  // The error replaces the hint rather than stacking under it: a password's hint and its
  // error state the same rule, and two lines saying it read as two problems.
  const showHint = Boolean(hint) && !error;
  const describedBy = [showHint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>

      <input
        {...input}
        id={id}
        ref={ref}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        className="rounded-[var(--kitup-radius-sm)] border border-[var(--kitup-color-muted)]/40 bg-white px-3 py-2 outline-none focus-visible:border-[var(--kitup-color-accent)] focus-visible:ring-2 focus-visible:ring-[var(--kitup-color-accent)]/40 aria-[invalid=true]:border-red-500"
      />

      {showHint ? (
        <p id={hintId} className="text-xs text-[var(--kitup-color-muted)]">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} role="alert" className="text-xs text-red-600">
          {error}
        </p>
      ) : null}
    </div>
  );
}
